import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { supabase } from '../../../lib/supabase';

interface Message {
  id: string;
  servicio: string;
  created_at: string;
}

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const StatsTab = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_contact_messages')
        .select('id, servicio, created_at')
        .order('created_at', { ascending: false });
      setMessages(data ?? []);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
      </div>
    );
  }

  const now = new Date();
  const thisMonth = messages.filter(m => {
    const d = new Date(m.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // Services chart
  const serviceCounts: Record<string, number> = {};
  for (const m of messages) {
    const s = m.servicio || 'Sin especificar';
    serviceCounts[s] = (serviceCounts[s] ?? 0) + 1;
  }
  const serviceData = Object.entries(serviceCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, count]) => ({
      name: name.length > 20 ? name.slice(0, 18) + '…' : name,
      count,
      pct: messages.length > 0 ? Math.round((count / messages.length) * 100) : 0,
    }));

  const topService = serviceData[0]?.name ?? '—';

  // Day of week chart
  const dayCounts: Record<string, number> = { Dom: 0, Lun: 0, Mar: 0, Mié: 0, Jue: 0, Vie: 0, Sáb: 0 };
  for (const m of messages) {
    const dow = DAYS_ES[new Date(m.created_at).getDay()];
    dayCounts[dow] = (dayCounts[dow] ?? 0) + 1;
  }
  const dayData = Object.entries(dayCounts).map(([name, count]) => ({ name, count }));
  const topDay = Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

  const statCards = [
    { label: 'Total mensajes', value: messages.length, icon: 'ri-mail-line' },
    { label: 'Este mes', value: thisMonth, icon: 'ri-calendar-check-line' },
    { label: 'Servicio top', value: topService, icon: 'ri-star-line', small: true },
    { label: 'Día más activo', value: topDay, icon: 'ri-time-line' },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-yellow-400/10 rounded-lg text-yellow-400">
                <i className={`${s.icon} text-sm`} />
              </div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider leading-tight">{s.label}</p>
            </div>
            <p className={`text-white font-black ${s.small ? 'text-sm' : 'text-2xl md:text-3xl'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
          <h3 className="text-white font-bold text-xs uppercase tracking-wider mb-5 flex items-center gap-2">
            <i className="ri-bar-chart-horizontal-line text-yellow-400" /> Servicios más solicitados
          </h3>
          {serviceData.length === 0 ? (
            <p className="text-zinc-600 text-sm">Sin datos aún</p>
          ) : (
            <div className="space-y-3">
              {serviceData.map((s) => (
                <div key={s.name}>
                  <div className="flex justify-between mb-1.5">
                    <span className="text-zinc-300 text-xs truncate max-w-[65%]">{s.name}</span>
                    <span className="text-yellow-400 text-xs font-bold">{s.count} ({s.pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full">
                    <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
          <h3 className="text-white font-bold text-xs uppercase tracking-wider mb-5 flex items-center gap-2">
            <i className="ri-bar-chart-box-line text-yellow-400" /> Actividad por día
          </h3>
          {messages.length === 0 ? (
            <p className="text-zinc-600 text-sm">Sin datos aún</p>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={dayData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff', fontSize: 11 }}
                  itemStyle={{ color: '#FBBF24', fontSize: 11 }}
                />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {dayData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.name === topDay ? '#FBBF24' : '#3f3f46'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
